import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  ControlCostMissingVariablesDocument,
  ControlCostMissingVariablesQuery,
  ControlCostMissingVariablesQueryVariables,
  useAddControlsFromLibraryMutation,
} from 'src/graphql/_generated';
import { findVariableByToken } from 'src/components/FormulaInput/shared/formula';
import useVariables from 'src/hooks/useVariables/useVariables';
import { useAuth } from 'src/state/auth';
import { showErrorToast } from 'src/components/Toast';
import { getErrorMessage } from 'src/utils/error';
import { logger } from 'src/utils/logger';
import { MissingVariablesModal } from './MissingVariablesModal';
import { useSaveMissingVariables } from './useSaveMissingVariables';
import { MissingVariableEntry } from './util';

interface UseAddControlsWithVariableReviewConfig {
  /** Per-site completion — the call sites genuinely differ (refetch keys, toasts, audit). */
  afterSuccess: (count: number) => void | Promise<void>;
  onError?: (err: unknown) => void;
}

interface RequestAddOpts {
  addRecommendedModifiers?: boolean;
}

const prettify = (name: string): string => {
  const display = name.replace(/_/g, ' ');
  return display.charAt(0).toUpperCase() + display.slice(1);
};

/**
 * Wraps `addControlsFromLibrary` with the Missing-Variables review (nudge): the BE
 * `controlCostMissingVariables` query reports which variables the selected templates'
 * cost formulas reference that are unpopulated in the workspace — when any, the modal
 * opens first (Save & Proceed populates them, Skip adds anyway). The BE copies the
 * template formulas onto the created controls (copy-once, CRPD-7222). The consumer
 * renders `reviewModal` in its tree.
 */
export const useAddControlsWithVariableReview = ({ afterSuccess, onError }: UseAddControlsWithVariableReviewConfig) => {
  const { workspaceId } = useAuth();
  const { variables } = useVariables({});
  const client = useApolloClient();
  const saveMissingVariables = useSaveMissingVariables({});
  const [addControlsFromLibrary] = useAddControlsFromLibraryMutation({ onError });

  const [review, setReview] = useState<{ ids: string[]; entries: MissingVariableEntry[]; opts?: RequestAddOpts } | null>(
    null,
  );
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleError = (err: unknown) => {
    if (onError) onError(err);
    else showErrorToast(getErrorMessage(err, 'Something went wrong'));
  };

  const doAdd = async (ids: string[], opts?: RequestAddOpts) => {
    setIsAdding(true);
    try {
      await addControlsFromLibrary({
        variables: {
          workspaceId,
          controlIds: ids.map((id) => ({ id })),
          addRecommendedModifiers: opts?.addRecommendedModifiers,
        },
      });
      await afterSuccess(ids.length);
    } finally {
      setIsAdding(false);
    }
  };

  const requestAdd = async (ids: string[], opts?: RequestAddOpts) => {
    // The modal maps BE variable ids to local variable metadata (currency, scope) —
    // with variables still loading, skip detection rather than risk a review that
    // saves duplicates. The add itself must never be blocked by a detection failure.
    if (variables) {
      try {
        setIsAdding(true);
        const { data } = await client.query<ControlCostMissingVariablesQuery, ControlCostMissingVariablesQueryVariables>(
          {
            query: ControlCostMissingVariablesDocument,
            variables: { workspaceId, frameworkControlIds: ids },
            fetchPolicy: 'no-cache',
          },
        );
        const entries: MissingVariableEntry[] = (data?.controlCostMissingVariables ?? []).map((missing) => {
          const variable = findVariableByToken(variables, String(missing.id)) ?? null;
          return {
            variable,
            token: String(missing.id),
            displayName: variable ? variable.alias ?? variable.name : prettify(missing.name),
          };
        });

        if (entries.length) {
          setReview({ ids, entries, opts });
          return;
        }
      } catch (err) {
        logger.data.warn('controlCostMissingVariables detection failed — adding without the review', err);
      } finally {
        setIsAdding(false);
      }
    }
    await doAdd(ids, opts);
  };

  const reviewModal = review ? (
    <MissingVariablesModal
      entries={review.entries}
      destination="rmp"
      plural={review.ids.length > 1}
      saving={isSaving || isAdding}
      onSaveAndProceed={async (values) => {
        setIsSaving(true);
        try {
          await saveMissingVariables(values);
          const { ids, opts } = review;
          setReview(null);
          await doAdd(ids, opts);
        } catch (err) {
          handleError(err);
        } finally {
          setIsSaving(false);
        }
      }}
      onSkip={async () => {
        const { ids, opts } = review;
        setReview(null);
        await doAdd(ids, opts);
      }}
      onClose={() => setReview(null)}
    />
  ) : null;

  return { requestAdd, reviewModal, isAdding };
};
